/*
 * File: InstructorNotes.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { useCallback, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useQueryClient } from '@tanstack/react-query';
import type { SerializedEditorState } from 'lexical';

import { MinimalUser } from 'api';
import { useUpdatePeriodEnrollmentNotes } from 'api/hooks/private/periods/UpdatePeriodEnrollmentNotes';
import { useUpdatePeriodEnrollmentPrivateNotes } from 'api/hooks/private/periods/UpdatePeriodEnrollmentPrivateNotes';

import { BasicRichTextEditor } from 'components/Editor';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { formatUpdateInfo } from '../../utils';

interface InstructorNotesProps {
  studentId: string;
  periodId: string;
  notes?: SerializedEditorState | null;
  notesUpdatedAt?: string | null;
  notesUpdatedBy?: MinimalUser | null;
  privateNotes?: SerializedEditorState | null;
  privateNotesUpdatedAt?: string | null;
  privateNotesUpdatedBy?: MinimalUser | null;
}

export default function InstructorNotes({
  studentId,
  periodId,
  notes,
  notesUpdatedAt,
  notesUpdatedBy,
  privateNotes,
  privateNotesUpdatedAt,
  privateNotesUpdatedBy,
}: InstructorNotesProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'notes' | 'private'>('notes');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const updateNotesMutation = useUpdatePeriodEnrollmentNotes({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_period', { id: periodId }] });
      setShowSuccessToast(true);
    },
    onError: (error) => {
      setErrorMessage(getApiErrorMessage(error, 'Failed to save notes'));
      setShowErrorToast(true);
    },
  });

  const updatePrivateNotesMutation = useUpdatePeriodEnrollmentPrivateNotes({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['course_period', { id: periodId }] });
      setShowSuccessToast(true);
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to save private notes');
      setShowErrorToast(true);
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'notes' | 'private') => {
    setActiveTab(newValue);
  };

  const handleSaveNotes = (notes: SerializedEditorState | undefined) => {
    if (notes) {
      updateNotesMutation.mutate({
        studentId,
        periodId,
        notes,
      });
    }
  };

  const handleSavePrivateNotes = (privateNotes: SerializedEditorState | undefined) => {
    if (privateNotes) {
      updatePrivateNotesMutation.mutate({
        studentId,
        periodId,
        privateNotes,
      });
    }
  };

  const handleCloseSuccessToast = useCallback(() => {
    setShowSuccessToast(false);
  }, []);

  const handleCloseErrorToast = useCallback(() => {
    setShowErrorToast(false);
  }, []);

  return (
    <Stack>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Notes" value="notes" />
          <Tab label="Private Notes" value="private" />
        </Tabs>
      </Box>

      {/*
        Both editors remain mounted using display: none instead of conditional rendering.
        This preserves editor state and unsaved changes when switching between tabs.
      */}
      <Box sx={{ display: activeTab === 'notes' ? 'block' : 'none' }}>
        <BasicRichTextEditor
          placeholder="Add notes about this drive session that will be visible to the student..."
          namespace="period-enrollment-notes"
          initialState={notes ?? undefined}
          showSaveButton
          onSave={handleSaveNotes}
          saveButtonText="Save Notes"
          isSaving={updateNotesMutation.isPending}
          updateInfo={formatUpdateInfo(notesUpdatedAt, notesUpdatedBy) ?? undefined}
        />
      </Box>

      <Box sx={{ display: activeTab === 'private' ? 'block' : 'none' }}>
        <BasicRichTextEditor
          placeholder="Add private notes that are only visible to instructors and admins..."
          namespace="period-enrollment-private-notes"
          initialState={privateNotes ?? undefined}
          showSaveButton
          onSave={handleSavePrivateNotes}
          saveButtonText="Save Private Notes"
          isSaving={updatePrivateNotesMutation.isPending}
          updateInfo={formatUpdateInfo(privateNotesUpdatedAt, privateNotesUpdatedBy) ?? undefined}
        />
      </Box>

      {/* Success toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={handleCloseSuccessToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccessToast} severity="success" variant="filled" sx={{ width: '100%' }}>
          Changes saved successfully
        </Alert>
      </Snackbar>

      {/* Error toast */}
      <Snackbar
        open={showErrorToast}
        autoHideDuration={5000}
        onClose={handleCloseErrorToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseErrorToast} severity="error" variant="filled" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
